// Pure scent projections (Tranche 3): reduce a chapter's links to per-verse
// "information scent" - how much is here, and of what kinds - BEFORE the
// learner taps Explore sources. DOM-free, network-free, deterministic.
//
// HONESTY RULES: every count flows through the SAME acceptSourceLink taxonomy
// gate AND the same display-mode filter the source panel uses, and the caller
// dedupes segments exactly like the panel does - so the per-family digits
// shown on the button always equal the panel's count. (The v4 parity dedupe
// removed the range-anchor ambiguity that originally forced word-tiers
// instead of numbers; the tier still gates rendering.) Zero accepted links = NO
// scent (absence, not "0").

import type { SourceLink } from "./sefaria.ts";
import { acceptSourceLink } from "./taxonomy.ts";
import { getSourceType } from "./typeSystem.ts";
import { shouldShowSourceForDisplayMode, type SourceDisplayMode } from "./sourceLang.ts";

// Distribute a chapter's ACCEPTED links to their verse refs. Verse-anchored
// links land on their anchorRef; range-anchored links land on every verse in
// Sefaria's own anchorRefExpanded (real field, never computed by us). Links
// with neither a verse anchor nor an expansion are dropped (no guessing).
export function distributeChapterLinks(links: SourceLink[]): Map<string, SourceLink[]> {
  const byVerse = new Map<string, SourceLink[]>();
  const add = (vref: string, l: SourceLink): void => {
    const arr = byVerse.get(vref);
    if (arr) arr.push(l);
    else byVerse.set(vref, [l]);
  };
  for (const l of links) {
    if (!acceptSourceLink(l)) continue;
    if (l.anchorRefExpanded && l.anchorRefExpanded.length > 0) {
      for (const vref of l.anchorRefExpanded) add(vref, l);
    } else {
      add(l.anchorRef, l);
    }
  }
  return byVerse;
}

// The links that COUNT under the current "Show sources" display mode - the
// same filter the panel applies, so scent and panel always agree.
export function scentVisible(links: readonly SourceLink[], mode: SourceDisplayMode): SourceLink[] {
  return links.filter((l) => shouldShowSourceForDisplayMode(l, mode));
}

// Tier thresholds: 1-3 a few, 4-9 some, 10+ many. 0 = no scent at all.
export type ScentTier = 0 | 1 | 2 | 3;
export function scentTier(count: number): ScentTier {
  if (count <= 0) return 0;
  if (count <= 3) return 1;
  if (count <= 9) return 2;
  return 3;
}

export interface ScentFamily {
  label: string; // honest type label ("Commentary", "Talmud", ...)
  colorVar: string; // the type ink CSS var
  count: number;
}

// Type families present, by descending count (full list - the caller caps the
// visible dots at 3 and uses the full list for the hover/SR breakdown).
export function scentFamilies(links: readonly SourceLink[]): ScentFamily[] {
  const byLabel = new Map<string, ScentFamily>();
  for (const l of links) {
    const t = getSourceType(l.category);
    const cur = byLabel.get(t.label);
    if (cur) cur.count++;
    else byLabel.set(t.label, { label: t.label, colorVar: t.colorVar, count: 1 });
  }
  return [...byLabel.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// The hover/screen-reader breakdown: "12 commentary, 3 midrash, 1 talmud".
export function scentBreakdown(families: readonly ScentFamily[]): string {
  return families.map((f) => `${f.count} ${f.label.toLowerCase()}`).join(", ");
}
